import { DOMAttributes, FocusableElement } from '@react-types/shared';
import React from 'react';
import { AriaListBoxOptions } from 'react-aria';
import { useResizeDetector } from 'react-resize-detector';
import { SelectState } from 'react-stately';
import SimpleBar from 'simplebar-react';
import Container from 'components/Container';
import { ListBoxData } from 'components/ListBox';
import 'simplebar-react/dist/simplebar.min.css';
import { SELECT_MENU_MAX_HEIGHT_PX } from 'components/Select/constants';
import * as S from './styles';

export type SelectMenuContentsProps<T extends ListBoxData> =
  DOMAttributes<FocusableElement> & {
    children?: React.ReactNode;
    className?: string;
    footer?: React.ReactNode;
    menuProps: AriaListBoxOptions<T>;
    scrollerClassName?: string;
    state: SelectState<T>;
  };

function ForwardedSelectMenuContents<T extends ListBoxData>(
  {
    children,
    className,
    footer,
    menuProps,
    scrollerClassName,
    state,
    ...domAttrs
  }: SelectMenuContentsProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.ReactElement | null {
  const { ref: footerRef, height: footerHeightPx = 0 } = useResizeDetector();

  return (
    <Container
      className={className}
      cornerRadius="16px"
      gutter={0}
      {...domAttrs}
    >
      <SimpleBar
        autoHide={false}
        className={scrollerClassName}
        scrollableNodeProps={{ ref }}
        style={{ maxHeight: SELECT_MENU_MAX_HEIGHT_PX - footerHeightPx }}
      >
        <S.ListBox {...menuProps} state={state} />
      </SimpleBar>
      {footer && <div ref={footerRef}>{footer}</div>}
    </Container>
  );
}

const SelectMenuContents = React.forwardRef(ForwardedSelectMenuContents);

export default SelectMenuContents;
